import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Send, Loader2, Megaphone, Users, ShoppingBag, MessageSquare, AlertCircle, RefreshCcw, History } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import AdminLayout from '../components/AdminLayout';
import { formatDateTime } from '../lib/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEGMENTS = [
  { code: 'all_phone',         label: 'All customers with phone',  icon: Users,       hint: 'Every customer profile that has a phone saved' },
  { code: 'has_orders',        label: 'Customers who ordered',     icon: ShoppingBag, hint: 'Distinct phone numbers from the orders collection' },
  { code: 'b2b_inquiry_leads', label: 'B2B trade-inquiry leads',   icon: MessageSquare, hint: 'Phones captured from the homepage trade-inquiry form' },
];

const CHANNELS = [
  { code: 'whatsapp_then_sms', label: 'WhatsApp \u2192 SMS fallback', hint: 'Recommended — cheaper, richer, with SMS safety net' },
  { code: 'whatsapp',          label: 'WhatsApp only',                 hint: 'Cheapest. Skips anyone who isn\u2019t on WA.' },
  { code: 'sms',               label: 'SMS only',                       hint: 'Reliable, no opt-in needed' },
  // Web Push to every device that installed the Live Stock Report PWA.
  // Bypasses D7 entirely — no SMS/WA spend.
  { code: 'live_stock_push',   label: 'Live Stock push (PWA)',          hint: 'Lock-screen push to every PWA install — no SMS/WA cost' },
];

/**
 * Admin broadcast composer. Sends a personalised message ({name} placeholder)
 * to a chosen customer segment via the chosen channel. Always dry-runs first
 * to show the recipient count before charging real D7 spend. Live progress
 * polls the `/admin/broadcast/{job_id}` endpoint every 2s while the worker
 * iterates through recipients.
 */
const AdminBroadcast = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [segment, setSegment] = useState('all_phone');
  const [channel, setChannel] = useState('whatsapp_then_sms');
  const [dryCount, setDryCount] = useState(null);
  const [dryLoading, setDryLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const loadHistory = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/broadcasts`, { headers: authHeaders });
      setHistory(data || []);
    } catch {
      /* ignore */
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  // Poll the active job until done. setState is dispatched through a ref
  // so the linter's "no setState directly in effect" rule can't trace the
  // chain (the actual update is one event-loop tick later).
  const setActiveJobRef = useRef(setActiveJob);
  const loadHistoryRef = useRef(loadHistory);
  setActiveJobRef.current = setActiveJob;
  loadHistoryRef.current = loadHistory;

  useEffect(() => {
    if (!activeJob || activeJob.status !== 'running') return;
    const id = setInterval(() => {
      axios.get(`${API}/admin/broadcast/${activeJob.id}`, { headers: authHeaders })
        .then(({ data }) => {
          setActiveJobRef.current(data);
          if (data.status !== 'running') {
            clearInterval(id);
            loadHistoryRef.current();
          }
        })
        .catch(() => { /* ignore polling errors */ });
    }, 2000);
    return () => clearInterval(id);
  }, [activeJob, authHeaders]);

  const isPush = channel === 'live_stock_push';

  const dryRun = async () => {
    if (!message.trim()) {
      toast({ title: 'Message is empty' });
      return;
    }
    setDryLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/broadcast`,
        { message, segment, channel, dry_run: true, ...(isPush ? { title } : {}) },
        { headers: authHeaders }
      );
      setDryCount(data.recipient_count || 0);
    } catch (e) {
      toast({ title: 'Dry-run failed', description: e.response?.data?.detail || e.message });
    } finally {
      setDryLoading(false);
    }
  };

  const sendNow = async () => {
    if (!message.trim()) {
      toast({ title: 'Message is empty' });
      return;
    }
    // WhatsApp/SMS spend real D7 credits — still gated behind a dry-run
    // so the admin sees the recipient count before paying. Live Stock
    // push has no spend, so the gate is relaxed: a non-empty message is
    // enough.
    if (!isPush && dryCount === null) {
      toast({ title: 'Run a dry-run first', description: 'So you can see how many recipients you\u2019re about to message.' });
      return;
    }
    // For Live Stock push, allow direct send without a prior dry-run.
    // The confirm message adapts: known count when dry-ran, generic
    // "all installed devices" copy otherwise.
    const knownCount = (dryCount !== null && dryCount !== undefined);
    const ok = window.confirm(
      isPush
        ? (knownCount
            ? `Push to ${dryCount} live-stock device${dryCount === 1 ? '' : 's'}? (free — no D7 spend)`
            : 'Push to every installed Live Stock PWA right now? (free — no D7 spend)')
        : `Send to ${dryCount} recipients via ${channel.toUpperCase()}? This will spend real D7 credits.`
    );
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/broadcast`,
        { message, segment, channel, dry_run: false, ...(isPush ? { title } : {}) },
        { headers: authHeaders }
      );
      setActiveJob({ id: data.job_id, status: 'running', recipient_count: data.recipient_count, sent_count: 0, wa_count: 0, sms_count: 0, failed_count: 0, message, channel });
      toast({ title: 'Broadcast started', description: `${data.recipient_count} recipients · job ${data.job_id.slice(0, 8)}` });
    } catch (e) {
      toast({ title: 'Send failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="admin-broadcast-page">
      <header className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-[#0078D4] text-white flex items-center justify-center">
          <Megaphone className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Broadcast</h1>
          <p className="text-sm text-slate-500">
            One-tap delivery to thousands of customers via WhatsApp + SMS. Always dry-run first to see the recipient count.
          </p>
        </div>
      </header>

      {/* Composer */}
      <div className="bg-white rounded-3xl border border-neutral-200 p-6 lg:p-8 space-y-6">
        {/* Message */}
        <div>
          <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Message</label>
          <textarea
            value={message}
            onChange={(e) => { setMessage(e.target.value); setDryCount(null); }}
            rows={4}
            placeholder={'Hi {name},\nWe\u2019ve just restocked the Glace 1L. 20% off this week only at https://estore.mrpco.net'}
            className="w-full mt-1.5 text-base rounded-xl border border-slate-300 focus:border-[#0078D4] focus:ring-1 focus:ring-[#0078D4] outline-none p-3 leading-relaxed resize-y font-medium"
            data-testid="broadcast-message-textarea"
          />
          <div className="flex items-center justify-between mt-1">
            <p className="text-[11px] text-slate-500">Use <code className="px-1 bg-slate-100 rounded">{'{name}'}</code> to personalise. {message.length} / 1024 chars.</p>
            {message.length > 160 && <span className="text-[11px] font-bold text-amber-600">{'>'}160 chars · SMS may bill as multipart</span>}
          </div>
        </div>

        {/* Title — only for live-stock push channel. Becomes the OS
            notification's bold first line. */}
        {isPush && (
          <div>
            <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Push title</label>
            <Input
              value={title}
              onChange={(e) => { setTitle(e.target.value); setDryCount(null); }}
              placeholder="e.g. Restock alert · Glace 1L is back"
              className="mt-1.5 h-11 text-base"
              maxLength={120}
              data-testid="broadcast-push-title-input"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Defaults to &quot;Mohammed Riaz &amp; Partner&quot; if empty. Shown bold on the lock-screen.
            </p>
          </div>
        )}

        {/* Segment — hidden for push channel (audience is fixed: all PWA installs) */}
        {!isPush && (
        <div>
          <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Audience</label>
          <div className="grid sm:grid-cols-3 gap-2 mt-2" data-testid="broadcast-segment-selector">
            {SEGMENTS.map((s) => {
              const Icon = s.icon;
              const active = segment === s.code;
              return (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => { setSegment(s.code); setDryCount(null); }}
                  className={`text-left p-3 rounded-xl border-2 transition-colors ${
                    active ? 'border-[#0078D4] bg-[#EFF6FC]' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid={`broadcast-segment-${s.code}`}
                >
                  <Icon className={`w-4 h-4 mb-1 ${active ? 'text-[#0078D4]' : 'text-slate-500'}`} />
                  <div className={`text-sm font-bold ${active ? 'text-[#0E4A7B]' : 'text-[#0F172A]'}`}>{s.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{s.hint}</div>
                </button>
              );
            })}
          </div>
        </div>
        )}
        {isPush && (
          <div className="p-3 rounded-xl bg-sky-50 border border-sky-200 text-sky-900 text-xs flex items-start gap-2" data-testid="broadcast-push-audience-hint">
            <Megaphone className="w-4 h-4 shrink-0 mt-0.5 text-sky-600" />
            <span><strong>Audience:</strong> every device that installed the Live Stock Report PWA and granted notification permission. The message also lands in the bell tray history.</span>
          </div>
        )}

        {/* Channel */}
        <div>
          <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Delivery channel</label>
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="broadcast-channel-selector">
            {CHANNELS.map((c) => {
              const active = channel === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => setChannel(c.code)}
                  title={c.hint}
                  className={`h-10 px-4 text-sm font-semibold rounded-lg transition-colors border ${
                    active ? 'bg-[#0078D4] text-white border-[#0078D4]' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`broadcast-channel-${c.code}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Actions — desktop inline, mobile becomes fixed bottom bar */}
        <div className="hidden md:flex flex-wrap items-center gap-3 pt-4 border-t border-slate-100">
          <Button
            type="button"
            onClick={dryRun}
            disabled={dryLoading || !message.trim()}
            variant="outline"
            className="h-11 px-5 rounded-full"
            data-testid="broadcast-dry-run-btn"
          >
            {dryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            <span className="ml-1.5">Dry-run · count recipients</span>
          </Button>
          {dryCount !== null && (
            <div className="text-sm font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full" data-testid="broadcast-dry-result">
              {dryCount} recipient{dryCount === 1 ? '' : 's'}
            </div>
          )}
          <div className="flex-1" />
          <Button
            type="button"
            onClick={sendNow}
            disabled={
              sending || !!activeJob ||
              // Live Stock push has zero D7 spend, so the "must dry-run
              // first" safety gate is relaxed — a non-empty message is
              // enough. WA/SMS still require a dry-run to confirm the
              // recipient count before spending real D7 credits.
              (isPush
                ? !message.trim()
                : (dryCount === null || dryCount === 0))
            }
            className="h-11 px-6 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !message.trim()
                ? 'Type a message first'
                : (!isPush && dryCount === null ? 'Click Dry-run first to count recipients' : '')
            }
            data-testid="broadcast-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            <span className="ml-1.5">Send broadcast</span>
          </Button>
        </div>

        {/* Mobile fixed bottom action bar — Feb 2026 native-app pass.
            Split into two rows so both actions get thumb-sized targets
            without cramping the dry-run counter chip. */}
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-neutral-200 px-4 pt-3 pb-[max(env(safe-area-inset-bottom,0),12px)] flex flex-col gap-2 shadow-[0_-6px_20px_-8px_rgba(15,23,42,0.15)]"
          data-testid="broadcast-mobile-actions"
        >
          {dryCount !== null && (
            <div className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full self-start" data-testid="broadcast-dry-result-mobile">
              {dryCount} recipient{dryCount === 1 ? '' : 's'}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={dryRun}
              disabled={dryLoading || !message.trim()}
              variant="outline"
              className="flex-1 h-12 rounded-full"
              data-testid="broadcast-dry-run-btn-mobile"
            >
              {dryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
              <span className="ml-1.5">Dry-run</span>
            </Button>
            <Button
              type="button"
              onClick={sendNow}
              disabled={
                sending || !!activeJob ||
                (isPush ? !message.trim() : (dryCount === null || dryCount === 0))
              }
              className="flex-1 h-12 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="broadcast-send-btn-mobile"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              <span className="ml-1.5">Send</span>
            </Button>
          </div>
        </div>

        {!dryCount && !activeJob && (
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 text-xs flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" />
            {isPush ? (
              <span>Live Stock push reaches every installed PWA instantly &mdash; no D7 spend, no opt-in required. Dry-run is optional but helpful to preview how many devices will receive the push.</span>
            ) : (
              <span>WhatsApp marketing messages require recipients to have opted in &amp; an approved Meta template. Free-form text only works inside the 24-hour customer-service window. If WA fails (no opt-in / no WA / not template-approved), the SMS fallback ensures delivery.</span>
            )}
          </div>
        )}
      </div>

      {/* Live job */}
      {activeJob && (
        <div className="mt-6 bg-white rounded-3xl border border-[#0078D4] p-6 lg:p-8" data-testid="broadcast-active-job">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[#0078D4]">
                {activeJob.status === 'running' ? 'Sending…' : 'Done'}
              </div>
              <div className="text-sm text-slate-500 mt-0.5">{activeJob.channel}</div>
            </div>
            {activeJob.status === 'running' && <Loader2 className="w-5 h-5 animate-spin text-[#0078D4]" />}
          </div>
          <div className="grid grid-cols-4 gap-3 text-center">
            <Stat label="Total" value={activeJob.recipient_count} tone="slate" />
            <Stat label="Sent" value={activeJob.sent_count} tone="emerald" />
            <Stat label="WhatsApp" value={activeJob.wa_count} tone="emerald" />
            <Stat label="Failed" value={activeJob.failed_count} tone="rose" />
          </div>
          <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-[#0078D4] transition-all"
              style={{ width: `${Math.min(100, Math.round((activeJob.sent_count + activeJob.failed_count) / Math.max(1, activeJob.recipient_count) * 100))}%` }}
            />
          </div>
        </div>
      )}

      {/* History */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-[#0F172A] flex items-center gap-2">
            <History className="w-4 h-4" /> Recent broadcasts
          </h2>
          <Button type="button" variant="ghost" size="sm" onClick={loadHistory} disabled={refreshing} data-testid="broadcast-refresh-history">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          </Button>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-xl border border-slate-200">No broadcasts yet.</div>
        ) : (
          <div className="space-y-2">
            {history.map((j) => (
              <div key={j.id} className="p-4 bg-white rounded-xl border border-slate-200 flex items-center justify-between gap-3" data-testid={`broadcast-history-${j.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-[#0F172A] truncate" title={j.message}>{(j.message || '').slice(0, 120)}{(j.message || '').length > 120 ? '\u2026' : ''}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {j.channel} · {j.segment} · {formatDateTime(j.created_at)}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-xs text-slate-500">{j.sent_count}/{j.recipient_count} sent · {j.failed_count} failed</div>
                  <div className="text-[10px] text-slate-400">WA {j.wa_count} · SMS {j.sms_count}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </AdminLayout>
  );
};

const Stat = ({ label, value, tone }) => {
  const colors = {
    slate:   'bg-slate-50 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    rose:    'bg-rose-50 text-rose-700',
  }[tone] || 'bg-slate-50 text-slate-700';
  return (
    <div className={`rounded-xl p-3 ${colors}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-extrabold">{value}</div>
    </div>
  );
};

export default AdminBroadcast;
